import React, { useState, useEffect, useCallback } from 'react';
import { Users, UserPlus, Trash2, Loader, CheckCircle, AlertCircle, Settings, X, Activity } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import teamService from '../services/teamService';
import './TeamPage.css';

const PERMISSION_LABELS = [
  { key: 'viewListings',   label: 'رؤية العروض' },
  { key: 'viewRequests',   label: 'رؤية الطلبات' },
  { key: 'addListings',    label: 'إضافة عروض' },
  { key: 'addRequests',    label: 'إضافة طلبات' },
  { key: 'communicate',   label: 'التواصل مع الزبائن' },
  { key: 'deleteListings', label: 'حذف العروض' },
];

const DEFAULT_PERMISSIONS = {
  viewListings: true,
  viewRequests: true,
  addListings: true,
  addRequests: true,
  communicate: true,
  deleteListings: false,
};

const TeamPage = ({ embedded = false }) => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState('');
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Permissions modal state
  const [permModal, setPermModal] = useState(null); // null or member object
  const [permDraft, setPermDraft] = useState({});
  const [savingPerms, setSavingPerms] = useState(false);
  const [permMsg, setPermMsg] = useState('');

  // Activity log state
  const [activeTab, setActiveTab] = useState('team'); // 'team' | 'activity'
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const fetchMembers = async () => {
    setLoadingMembers(true);
    const res = await teamService.getTeam();
    if (res.success) setMembers(res.data || []);
    setLoadingMembers(false);
  };

  useEffect(() => { fetchMembers(); }, []);

  const fetchActivityLogs = useCallback(async () => {
    setLoadingLogs(true);
    const res = await teamService.getActivityLogs();
    if (res.success) setActivityLogs(res.data?.logs || []);
    setLoadingLogs(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'activity') fetchActivityLogs();
  }, [activeTab, fetchActivityLogs]);

  const handleInvite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    setSuccessMsg('');
    setErrorMsg('');
    const res = await teamService.inviteMember(email.trim());
    if (res.success) {
      setSuccessMsg(res.message || 'تم إرسال الدعوة بنجاح');
      setEmail('');
      setTimeout(() => setSuccessMsg(''), 4000);
    } else {
      setErrorMsg(res.message || 'حدث خطأ، حاول مرة أخرى');
      setTimeout(() => setErrorMsg(''), 4000);
    }
    setInviting(false);
  };

  const handleRemove = async (id) => {
    setRemovingId(id);
    const res = await teamService.removeMember(id);
    if (res.success) {
      setMembers((prev) => prev.filter((m) => m._id !== id));
    } else {
      setErrorMsg(res.message || 'حدث خطأ، حاول مرة أخرى');
      setTimeout(() => setErrorMsg(''), 4000);
    }
    setRemovingId(null);
  };

  const openPermModal = (member) => {
    setPermModal(member);
    setPermDraft({ ...DEFAULT_PERMISSIONS, ...(member.permissions || {}) });
    setPermMsg('');
  };

  const closePermModal = () => {
    setPermModal(null);
    setPermDraft({});
    setPermMsg('');
  };

  const handleSavePerms = async () => {
    if (!permModal) return;
    setSavingPerms(true);
    setPermMsg('');
    const res = await teamService.updatePermissions(permModal._id, permDraft);
    if (res.success) {
      setMembers((prev) => prev.map((m) => m._id === permModal._id ? res.data : m));
      setPermMsg('success');
      setTimeout(closePermModal, 1200);
    } else {
      setPermMsg(res.message || 'حدث خطأ');
    }
    setSavingPerms(false);
  };

  // Sub-accounts cannot access this page
  if (user?.accountType === 'sub') {
    return (
      <div className="team-page" dir="rtl">
        <div className="team-no-access">
          <AlertCircle size={36} />
          <p>هذه الصفحة متاحة للحسابات الرئيسية فقط</p>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="team-page" dir="rtl">
      {!embedded && (
        <div className="page-header">
          <h1><Users size={28} />فريقي</h1>
          <p className="page-subtitle">أضف زملاءك وأدر فريقك من هنا</p>
        </div>
      )}

      {/* Tabs */}
      <div className="team-tabs">
        <button className={`team-tab ${activeTab === 'team' ? 'active' : ''}`} onClick={() => setActiveTab('team')}>
          <Users size={16} /> الفريق
        </button>
        <button className={`team-tab ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => setActiveTab('activity')}>
          <Activity size={16} /> سجل النشاط
        </button>
      </div>

      {/* Activity Log Tab */}
      {activeTab === 'activity' && (
        <div className="team-card">
          <h2 className="team-section-title"><Activity size={20} />سجل نشاط الفريق</h2>
          {loadingLogs ? (
            <div className="team-loading"><Loader size={24} className="spin" /><span>جاري التحميل...</span></div>
          ) : activityLogs.length === 0 ? (
            <div className="empty-state"><Activity size={40} /><p>لا يوجد نشاط مسجل بعد</p></div>
          ) : (
            <div className="activity-log-table-wrap">
              <table className="activity-log-table">
                <thead>
                  <tr>
                    <th>العضو</th>
                    <th>النشاط</th>
                    <th>التفاصيل</th>
                    <th>التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {activityLogs.map((log) => (
                    <tr key={log._id}>
                      <td>{log.memberName}</td>
                      <td><span className={`activity-badge activity-${log.targetType}`}>{log.action}</span></td>
                      <td className="activity-title">{log.targetTitle}</td>
                      <td className="activity-date">{formatDate(log.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Team Tab Content */}
      {activeTab === 'team' && <>

      {/* Invite Section */}
      <div className="team-card">
        <h2 className="team-section-title"><UserPlus size={20} />دعوة عضو جديد</h2>
        <div className="invite-row">
          <input
            type="email"
            className="form-control"
            placeholder="أدخل بريد الزميل"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
            disabled={inviting}
          />
          <button
            className="btn btn-primary invite-btn"
            onClick={handleInvite}
            disabled={inviting || !email.trim()}
          >
            {inviting ? <Loader size={16} className="spin" /> : 'إرسال دعوة'}
          </button>
        </div>
        {successMsg && (
          <div className="team-message success-message"><CheckCircle size={16} />{successMsg}</div>
        )}
        {errorMsg && (
          <div className="team-message error-message"><AlertCircle size={16} />{errorMsg}</div>
        )}
      </div>

      {/* Members Section */}
      <div className="team-card">
        <h2 className="team-section-title"><Users size={20} />أعضاء الفريق</h2>
        {loadingMembers ? (
          <div className="team-loading"><Loader size={24} className="spin" /><span>جاري التحميل...</span></div>
        ) : members.length === 0 ? (
          <div className="empty-state"><Users size={40} /><p>لا يوجد أعضاء في فريقك بعد</p></div>
        ) : (
          <ul className="members-list">
            {members.map((member) => (
              <li key={member._id} className="member-card">
                <div className="member-avatar">
                  {member.avatar
                    ? <img src={member.avatar} alt={member.name} />
                    : <span>{member.name?.charAt(0) || '؟'}</span>}
                </div>
                <div className="member-info">
                  <strong>{member.name}</strong>
                  <span>{member.email}</span>
                </div>
                <div className="member-actions">
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => openPermModal(member)}
                    title="إدارة الصلاحيات"
                  >
                    <Settings size={14} />
                    الصلاحيات
                  </button>
                  <button
                    className="btn btn-danger btn-sm remove-btn"
                    onClick={() => handleRemove(member._id)}
                    disabled={removingId === member._id}
                  >
                    {removingId === member._id
                      ? <Loader size={14} className="spin" />
                      : <><Trash2 size={14} />إزالة</>}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      </> /* end team tab */}

      {/* Permissions Modal */}
      {permModal && (
        <div className="modal-overlay" onClick={closePermModal}>
          <div className="modal-box perm-modal" dir="rtl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>صلاحيات {permModal.name}</h3>
              <button className="modal-close-btn" onClick={closePermModal}><X size={18} /></button>
            </div>
            <div className="perm-list">
              {PERMISSION_LABELS.map(({ key, label }) => (
                <label key={key} className="perm-toggle-row">
                  <span className="perm-label">{label}</span>
                  <div
                    className={`toggle-switch ${permDraft[key] ? 'on' : 'off'}`}
                    onClick={() => setPermDraft((prev) => ({ ...prev, [key]: !prev[key] }))}
                  >
                    <div className="toggle-thumb" />
                  </div>
                </label>
              ))}
            </div>
            {permMsg === 'success' ? (
              <div className="team-message success-message"><CheckCircle size={16} />تم الحفظ بنجاح</div>
            ) : permMsg ? (
              <div className="team-message error-message"><AlertCircle size={16} />{permMsg}</div>
            ) : null}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closePermModal}>إلغاء</button>
              <button className="btn btn-primary" onClick={handleSavePerms} disabled={savingPerms}>
                {savingPerms ? <Loader size={15} className="spin" /> : 'حفظ الصلاحيات'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPage;
